import { useRef, useState } from "react";
import { Link2, Upload } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const fmtSize = (b) => (b >= 1024 ** 3 ? `${(b / 1024 ** 3).toFixed(2)} GB` : `${Math.round(b / 1024 ** 2)} MB`);
const inputCls = "h-10 w-full rounded-lg border border-slate-700 bg-[#0B0F19] px-3 text-sm text-white outline-none focus:border-[#FF2D78]";

const putChunk = async (id, i, blob) => {
  for (let attempt = 0; ; attempt++) {
    try { return await api.put(`/admin/video-uploads/${id}/chunks/${i}`, blob, { headers: { "Content-Type": "application/octet-stream" } }); }
    catch (e) { if (attempt >= 3) throw e; await new Promise((r) => setTimeout(r, 1500 * (attempt + 1))); }
  }
};

const uploadFile = async (file, onProgress) => {
  const key = `khn-video-upload:${file.name}:${file.size}:${file.lastModified}`;
  let up = null;
  const saved = localStorage.getItem(key);
  if (saved) up = await api.get(`/admin/video-uploads/${saved}`).then((r) => r.data).catch(() => null);
  if (!up || !["uploading", "ready"].includes(up.status)) {
    up = (await api.post("/admin/video-uploads", { filename: file.name, size: file.size })).data;
    localStorage.setItem(key, up.id);
  }
  if (up.status === "ready") { onProgress(1); return up.id; }
  const done = new Set(up.received);
  const todo = [...Array(up.chunks).keys()].filter((i) => !done.has(i));
  onProgress(done.size / up.chunks);
  let next = 0;
  const worker = async () => {
    while (next < todo.length) {
      const i = todo[next++];
      await putChunk(up.id, i, file.slice(i * up.chunk_size, Math.min(file.size, (i + 1) * up.chunk_size)));
      done.add(i);
      onProgress(done.size / up.chunks);
    }
  };
  await Promise.all([worker(), worker(), worker()]);
  await api.post(`/admin/video-uploads/${up.id}/complete`);
  localStorage.removeItem(key);
  return up.id;
};

export const VideoSourcePicker = ({ value, onChange, testPrefix = "match" }) => {
  const [progress, setProgress] = useState(null);
  const [file, setFile] = useState(null);
  const [err, setErr] = useState(null);
  const ref = useRef();
  const pick = async (f) => {
    if (!f) return;
    setFile(f); setErr(null); setProgress(0);
    onChange({ ...value, upload_id: null, uploading: true });
    try {
      const id = await uploadFile(f, setProgress);
      onChange({ ...value, mode: "upload", upload_id: id, uploading: false });
    } catch (e) {
      setErr(`${formatApiError(e)}. Pick the same file again to resume.`);
      onChange({ ...value, upload_id: null, uploading: false });
    }
  };
  const mode = value.mode;
  return (
    <div data-testid={`${testPrefix}-video-source`} className="space-y-2">
      <div className="inline-flex gap-1 rounded-lg border border-slate-800 bg-[#0B0F19] p-1">
        {[["upload", "Upload file", Upload], ["link", "Paste link", Link2]].map(([k, l, Icon]) => (
          <button key={k} type="button" data-testid={`${testPrefix}-video-mode-${k}`} onClick={() => onChange({ ...value, mode: k })}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs transition-colors duration-200 ${mode === k ? "bg-[#FF2D78] text-white" : "text-slate-400 hover:text-white"}`}>
            <Icon className="h-3.5 w-3.5" />{l}
          </button>
        ))}
      </div>
      {mode === "link" ? (
        <>
          <input data-testid={`${testPrefix}-video-url-input`} value={value.video_url} onChange={(e) => onChange({ ...value, video_url: e.target.value })}
            placeholder="Direct video file link (Dropbox, Google Drive or cloud storage)" className={inputCls} />
          <p className="text-[11px] text-slate-500">YouTube, Vimeo and Hudl page links won't work. The link must download the video file itself.</p>
        </>
      ) : (
        <div>
          <input ref={ref} type="file" accept=".mp4,.mov,.mkv,.m4v,video/*" className="hidden" data-testid={`${testPrefix}-video-file-input`}
            onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
          <button type="button" data-testid={`${testPrefix}-video-choose-button`} onClick={() => ref.current?.click()} disabled={value.uploading}
            className="rounded-lg border border-dashed border-slate-600 px-4 py-2 text-sm text-slate-300 hover:border-[#FF2D78] disabled:opacity-50">
            {file ? "Choose a different file" : "Choose match video (.mp4, .mov, .mkv, up to 4 GB)"}
          </button>
          {file && progress !== null && (
            <div className="mt-2">
              <div className="flex justify-between font-data text-[11px] text-slate-400">
                <span className="truncate">{file.name} · {fmtSize(file.size)}</span>
                <span data-testid={`${testPrefix}-video-progress`}>{value.upload_id ? "Uploaded" : `${Math.floor(progress * 100)}%`}</span>
              </div>
              <div className="mt-1 h-1 overflow-hidden rounded bg-slate-800">
                <div className="h-full bg-[#FF2D78] transition-[width] duration-300" style={{ width: `${progress * 100}%` }} />
              </div>
            </div>
          )}
        </div>
      )}
      {(err || value.error) && <p data-testid={`${testPrefix}-video-error`} className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">{err || value.error}</p>}
    </div>
  );
};

export const videoReady = (v) => (v.mode === "upload" ? !!v.upload_id : !!v.video_url?.trim());
export const videoPayload = (v) => (v.mode === "upload" ? { upload_id: v.upload_id } : { video_url: v.video_url.trim() });
export const EMPTY_VIDEO = { mode: "upload", video_url: "", upload_id: null, uploading: false, error: null };
